// Standardized Header Mapping Utility
// Ensures consistent column header names across all Excel components

export interface HeaderMapping {
  particulars: string[];
  assignedTo: string[];
  title: string[];
  description: string[];
  status: string[];
  priority: string[];
  category: string[];
  date: string[];
  dueDate: string[];
  createdBy: string[];
  lastUpdated: string[];
  attachments: string[];
  notes: string[];
  actions: string[];
}

export const STANDARD_HEADERS: HeaderMapping = {
  particulars: ['particulars', 'task name', 'task', 'title', 'item'],
  assignedTo: ['concerned person', 'assigned to', 'assigned', 'responsible', 'owner'],
  title: ['title', 'task name', 'particulars', 'task', 'name'],
  description: ['description', 'details', 'notes', 'summary'],
  status: ['status', 'state', 'progress'],
  priority: ['priority', 'importance', 'level'],
  category: ['category', 'type', 'classification', 'group'],
  date: ['date', 'due date', 'deadline', 'completion date', 'end date'],
  dueDate: ['due date', 'deadline', 'due', 'target date'],
  createdBy: ['created by', 'author', 'creator', 'created'],
  lastUpdated: ['last updated', 'updated', 'modified', 'changed'],
  attachments: ['attachments', 'files', 'documents', 'files'],
  notes: ['notes', 'comments', 'remarks', 'description'],
  actions: ['actions', 'options', 'menu', 'tools']
};

export class HeaderMapper {
  /**
   * Find column index by multiple possible header names
   * @param headerRow - Array of header cells
   * @param possibleNames - Array of possible header names to search for
   * @returns Column index or -1 if not found
   */
  static findColumnIndex(headerRow: any[], possibleNames: string[]): number {
    if (!headerRow || !Array.isArray(headerRow)) return -1;
    
    for (const headerName of possibleNames) {
      const index = headerRow.findIndex((cell: any) => {
        if (!cell?.value) return false;
        const cellValue = cell.value.toString().toLowerCase().trim();
        const searchValue = headerName.toLowerCase().trim();
        return cellValue.includes(searchValue);
      });
      if (index !== -1) return index;
    }
    return -1;
  }

  /**
   * Get all column mappings for a header row
   * @param headerRow - Array of header cells
   * @returns Object with column indices for each standard field
   */
  static getAllColumnMappings(headerRow: any[]): Record<string, number> {
    return {
      particulars: this.findColumnIndex(headerRow, STANDARD_HEADERS.particulars),
      assignedTo: this.findColumnIndex(headerRow, STANDARD_HEADERS.assignedTo),
      title: this.findColumnIndex(headerRow, STANDARD_HEADERS.title),
      description: this.findColumnIndex(headerRow, STANDARD_HEADERS.description),
      status: this.findColumnIndex(headerRow, STANDARD_HEADERS.status),
      priority: this.findColumnIndex(headerRow, STANDARD_HEADERS.priority),
      category: this.findColumnIndex(headerRow, STANDARD_HEADERS.category),
      date: this.findColumnIndex(headerRow, STANDARD_HEADERS.date),
      dueDate: this.findColumnIndex(headerRow, STANDARD_HEADERS.dueDate),
      createdBy: this.findColumnIndex(headerRow, STANDARD_HEADERS.createdBy),
      lastUpdated: this.findColumnIndex(headerRow, STANDARD_HEADERS.lastUpdated),
      attachments: this.findColumnIndex(headerRow, STANDARD_HEADERS.attachments),
      notes: this.findColumnIndex(headerRow, STANDARD_HEADERS.notes),
      actions: this.findColumnIndex(headerRow, STANDARD_HEADERS.actions)
    };
  }

  /**
   * Get cell value from row using column mapping
   * @param row - Array of row cells
   * @param columnIndex - Column index to get value from
   * @returns Cell value or empty string
   */
  static getCellValue(row: any[], columnIndex: number): string {
    if (columnIndex === -1 || !row || !Array.isArray(row) || columnIndex >= row.length) {
      return '';
    }
    const cell = row[columnIndex];
    return cell?.value ? cell.value.toString().trim() : '';
  }

  /**
   * Extract task data from Excel row using column mappings
   * @param row - Array of row cells
   * @param mappings - Column mappings object
   * @returns Task data object
   */
  static extractTaskData(row: any[], mappings: Record<string, number>): {
    particulars: string;
    assignedTo: string;
    title: string;
    description: string;
    status: string;
    priority: string;
    category: string;
    date: string;
    dueDate: string;
    createdBy: string;
    lastUpdated: string;
    attachments: string;
    notes: string;
  } {
    return {
      particulars: this.getCellValue(row, mappings.particulars),
      assignedTo: this.getCellValue(row, mappings.assignedTo),
      title: this.getCellValue(row, mappings.title),
      description: this.getCellValue(row, mappings.description),
      status: this.getCellValue(row, mappings.status),
      priority: this.getCellValue(row, mappings.priority),
      category: this.getCellValue(row, mappings.category),
      date: this.getCellValue(row, mappings.date),
      dueDate: this.getCellValue(row, mappings.dueDate),
      createdBy: this.getCellValue(row, mappings.createdBy),
      lastUpdated: this.getCellValue(row, mappings.lastUpdated),
      attachments: this.getCellValue(row, mappings.attachments),
      notes: this.getCellValue(row, mappings.notes)
    };
  }

  /**
   * Log column mapping results for debugging
   * @param mappings - Column mappings object
   * @param sheetName - Name of the sheet (for logging)
   */
  static logMappings(mappings: Record<string, number>, sheetName: string = 'Unknown'): void {
    console.log(`📊 Header mappings for sheet "${sheetName}":`);
    Object.entries(mappings).forEach(([field, index]) => {
      if (index !== -1) {
        console.log(`  ✅ ${field}: column ${index}`);
      } else {
        console.log(`  ❌ ${field}: not found`);
      }
    });
  }
}

export default HeaderMapper;














